import { z } from 'zod'
import { DateStringSchema } from './booking'
import { LICENCE_TYPES } from './licences'

// The club's exams: knowledge tests and onboarding checks (the exam schema,
// issue #223). Keep it in step with V1580-V1650 in db/flyway/schema/sql.
//
// An exam is generic on purpose -- the flight school can use it too. A 'quiz'
// is multiple choice, one right answer in four, and is marked by the system
// when submitted. A 'checklist' is a list of things to tick with no answers;
// an examiner signs it pass or fail.
//
// Everything a member reads is a LocalizedText: a map of ISO 639-1 language
// code to text. The rules that both the API and the browser apply -- marking,
// the deadline and the countdown warnings, who may start an attempt -- are
// pure and live here, so a phone shows exactly what the server will enforce.

export const EXAM_KINDS = ['quiz', 'checklist'] as const
export type ExamKind = (typeof EXAM_KINDS)[number]
export const ExamKindSchema = z.enum(EXAM_KINDS)

export const QUESTION_KINDS = ['choice', 'tick'] as const
export type QuestionKind = (typeof QUESTION_KINDS)[number]

export const ATTEMPT_STATUSES = ['in_progress', 'submitted', 'expired'] as const
export type AttemptStatus = (typeof ATTEMPT_STATUSES)[number]

// 'pending' = submitted, awaiting an examiner's signature.
export const ATTEMPT_RESULTS = ['pass', 'fail', 'pending'] as const
export type AttemptResult = (typeof ATTEMPT_RESULTS)[number]

export const EXAM_CHOICES_PER_QUESTION = 4
export const DEFAULT_PASS_MARK_PCT = 70

// ---------------------------------------------------------------------------
// Localised text

export type LocalizedText = Record<string, string>

export const LangCodeSchema = z.string().regex(/^[a-z]{2}$/, 'Expected a two-letter ISO 639-1 language code')

export const LocalizedTextSchema = z
  .record(LangCodeSchema, z.string().trim().min(1).max(2000))
  .refine((t) => Object.keys(t).length > 0, 'At least one language is required')

// The reader's language, else the exam's default, else Finnish, else any text
// there is. Empty only for an empty map.
export function resolveText(text: LocalizedText | null | undefined, preferred: string, fallback = 'fi'): string {
  if (!text) return ''
  return text[preferred] ?? text[fallback] ?? text.fi ?? Object.values(text)[0] ?? ''
}

// ---------------------------------------------------------------------------
// Marking

export interface MarkableQuestion {
  questionId: string
  kind: QuestionKind
  correctChoiceId: string | null
}

export interface GivenAnswer {
  choiceId?: string | null
  ticked?: boolean | null
}

export interface Marking {
  score: number
  maxScore: number
  // Whole percent, rounded down so 69.9% is never a pass at 70.
  pct: number
  // null for an exam with nothing to mark (a checklist): an examiner decides.
  result: 'pass' | 'fail' | null
}

// A quiz scores one point per 'choice' question whose picked choice is the
// right one; a question left blank scores nothing. A checklist (no choice
// questions) has no automatic result.
export function markAttempt(
  questions: MarkableQuestion[],
  answers: Record<string, GivenAnswer | undefined>,
  passMarkPct: number,
): Marking {
  const markable = questions.filter((q) => q.kind === 'choice')
  const maxScore = markable.length
  if (maxScore === 0) return { score: 0, maxScore: 0, pct: 0, result: null }
  const score = markable.filter((q) => q.correctChoiceId !== null && answers[q.questionId]?.choiceId === q.correctChoiceId).length
  const pct = Math.floor((score * 100) / maxScore)
  return { score, maxScore, pct, result: pct >= passMarkPct ? 'pass' : 'fail' }
}

// ---------------------------------------------------------------------------
// The clock

// ISO instant the attempt must be submitted by; null = untimed.
export function attemptDeadline(startedAt: string, timeLimitMinutes: number | null): string | null {
  if (timeLimitMinutes === null) return null
  return new Date(Date.parse(startedAt) + timeLimitMinutes * 60_000).toISOString()
}

export function isExpired(expiresAt: string | null, now: string): boolean {
  return expiresAt !== null && Date.parse(now) >= Date.parse(expiresAt)
}

export function remainingSeconds(expiresAt: string | null, now: string): number | null {
  if (expiresAt === null) return null
  return Math.max(0, Math.ceil((Date.parse(expiresAt) - Date.parse(now)) / 1000))
}

export type CountdownWarningKind = 'half' | 'threeQuarters' | 'lastFive'

export interface CountdownWarning {
  kind: CountdownWarningKind
  // Seconds from the start at which it fires.
  atSeconds: number
}

const WARNING_MERGE_SECONDS = 60

// The warnings for a timed exam: half-way, 75% and the last five minutes.
// A short exam would fire two almost together (at 10 minutes the half-way
// mark IS the five-minute mark), so warnings within a minute of one another
// collapse into the later, more urgent one; one that would fire at or before
// the start is dropped.
export function countdownWarnings(timeLimitMinutes: number): CountdownWarning[] {
  const total = timeLimitMinutes * 60
  const candidates = ([
    { kind: 'half', atSeconds: Math.round(total / 2) },
    { kind: 'threeQuarters', atSeconds: Math.round((total * 3) / 4) },
    { kind: 'lastFive', atSeconds: total - 300 },
  ] as CountdownWarning[])
    .filter((w) => w.atSeconds > 0)
    .sort((a, b) => a.atSeconds - b.atSeconds)
  const kept: CountdownWarning[] = []
  for (const w of candidates) {
    const last = kept[kept.length - 1]
    if (last && w.atSeconds - last.atSeconds < WARNING_MERGE_SECONDS) kept[kept.length - 1] = w
    else kept.push(w)
  }
  return kept
}

// The most recent warning already reached after `elapsedSeconds`, or null.
export function currentWarning(timeLimitMinutes: number, elapsedSeconds: number): CountdownWarning | null {
  const reached = countdownWarnings(timeLimitMinutes).filter((w) => w.atSeconds <= elapsedSeconds)
  return reached[reached.length - 1] ?? null
}

// ---------------------------------------------------------------------------
// Validity and who may sit

// The date a pass lapses: `months` calendar months after `date` (a day that
// does not exist in the target month, the 31st, becomes the month's last);
// null when a pass never lapses.
export function passValidUntil(date: string, months: number | null): string | null {
  if (months === null) return null
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  const first = new Date(Date.UTC(y, m - 1 + months, 1))
  const lastDay = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate()
  first.setUTCDate(Math.min(d, lastDay))
  return first.toISOString().slice(0, 10)
}

export interface AttemptSummary {
  status: AttemptStatus
  result: AttemptResult | null
  validUntil: string | null
}

// A pass counts until the day before valid_until... through valid_until.
export function isCurrentPass(a: Pick<AttemptSummary, 'result' | 'validUntil'>, today: string): boolean {
  return a.result === 'pass' && (a.validUntil === null || a.validUntil >= today)
}

export type StartBlocker = 'in_progress' | 'awaiting_signature' | 'already_passed' | 'retake_not_allowed'

// Why a member may not start another attempt now, or null when they may.
// `attempts` is all the member's attempts at the exam.
export function startBlocker(allowRetake: boolean, attempts: AttemptSummary[], today: string): StartBlocker | null {
  if (attempts.some((a) => a.status === 'in_progress')) return 'in_progress'
  if (attempts.some((a) => a.result === 'pending')) return 'awaiting_signature'
  if (attempts.some((a) => isCurrentPass(a, today))) return 'already_passed'
  if (!allowRetake && attempts.some((a) => a.result === 'fail')) return 'retake_not_allowed'
  return null
}

export interface HeldLicence {
  licenceType: string
  held: boolean | null
  expiryDate: string | null
}

// The licence types the exam requires that the member does not hold in date.
// `held` is a tri-state: NULL or true = holds it. A member may have several
// rows of a type (more than one medical): any one in date will do.
export function missingLicences(required: string[], licences: HeldLicence[], today: string): string[] {
  return required.filter(
    (type) => !licences.some((l) => l.licenceType === type && l.held !== false && (l.expiryDate === null || l.expiryDate >= today)),
  )
}

// ---------------------------------------------------------------------------
// Import / editing contract

const SlugSchema = z.string().regex(/^[a-z][a-z0-9-]{1,59}$/, 'Lower case letters, digits and hyphens')

export const ImportChoiceSchema = z.object({
  text: LocalizedTextSchema,
  correct: z.boolean().optional(),
})

export const ImportQuestionSchema = z.object({
  // Key of a section declared in `sections`; omitted = top level.
  section: z.string().min(1).max(40).optional(),
  text: LocalizedTextSchema,
  choices: z.array(ImportChoiceSchema).optional(),
})

export const ImportSectionSchema = z.object({
  key: z.string().min(1).max(40),
  title: LocalizedTextSchema,
})

// What an admin uploads (and what the editor saves). Question order is array
// order. For a 'quiz' every question has exactly four choices, one correct;
// for a 'checklist' no question has choices.
export const ExamContentSchema = z
  .object({
    sections: z.array(ImportSectionSchema).max(40).default([]),
    questions: z.array(ImportQuestionSchema).min(1).max(300),
  })

export const ExamSettingsSchema = z.object({
  title: LocalizedTextSchema,
  description: z.record(LangCodeSchema, z.string().max(2000)).default({}),
  defaultLocale: LangCodeSchema.default('fi'),
  timeLimitMinutes: z.number().int().min(1).max(600).nullable().default(null),
  resumable: z.boolean().default(true),
  passMarkPct: z.number().int().min(0).max(100).default(DEFAULT_PASS_MARK_PCT),
  allowRetake: z.boolean().default(true),
  validMonths: z.number().int().min(1).max(120).nullable().default(null),
  requiredLicences: z.array(z.enum(LICENCE_TYPES)).default([]),
  active: z.boolean().default(true),
})
export type ExamSettings = z.infer<typeof ExamSettingsSchema>

export const ExamImportSchema = z
  .object({ slug: SlugSchema, kind: ExamKindSchema })
  .merge(ExamSettingsSchema)
  .merge(ExamContentSchema)
  .superRefine((exam, ctx) => validateContent(exam.kind, exam, ctx))
export type ExamImport = z.infer<typeof ExamImportSchema>

export const ExamDraftSchema = ExamContentSchema.extend({ kind: ExamKindSchema }).superRefine((exam, ctx) =>
  validateContent(exam.kind, exam, ctx),
)
export type ExamDraftInput = z.infer<typeof ExamDraftSchema>

function validateContent(kind: ExamKind, exam: z.infer<typeof ExamContentSchema>, ctx: z.RefinementCtx): void {
  const sectionKeys = new Set<string>()
  exam.sections.forEach((s, i) => {
    if (sectionKeys.has(s.key)) ctx.addIssue({ code: 'custom', path: ['sections', i, 'key'], message: `Duplicate section key "${s.key}"` })
    sectionKeys.add(s.key)
  })
  exam.questions.forEach((q, i) => {
    if (q.section !== undefined && !sectionKeys.has(q.section)) {
      ctx.addIssue({ code: 'custom', path: ['questions', i, 'section'], message: `Unknown section "${q.section}"` })
    }
    const choices = q.choices ?? []
    if (kind === 'checklist') {
      if (choices.length > 0) ctx.addIssue({ code: 'custom', path: ['questions', i, 'choices'], message: 'A checklist item has no choices' })
      return
    }
    if (choices.length !== EXAM_CHOICES_PER_QUESTION) {
      ctx.addIssue({ code: 'custom', path: ['questions', i, 'choices'], message: `A question needs exactly ${EXAM_CHOICES_PER_QUESTION} choices` })
    } else if (choices.filter((c) => c.correct).length !== 1) {
      ctx.addIssue({ code: 'custom', path: ['questions', i, 'choices'], message: 'Exactly one choice must be correct' })
    }
  })
}

// Languages in use anywhere in the exam, and the (language, where) gaps: a
// text present in some languages but missing in another that the exam
// otherwise has. Warnings, not errors -- an exam may go out half translated.
export function translationGaps(exam: {
  title: LocalizedText
  sections: { title: LocalizedText }[]
  questions: { text: LocalizedText; choices?: { text: LocalizedText }[] }[]
}): { locales: string[]; gaps: { locale: string; where: string }[] } {
  const texts: { where: string; text: LocalizedText }[] = [{ where: 'title', text: exam.title }]
  exam.sections.forEach((s, i) => texts.push({ where: `section ${i + 1}`, text: s.title }))
  exam.questions.forEach((q, i) => {
    texts.push({ where: `question ${i + 1}`, text: q.text })
    ;(q.choices ?? []).forEach((c, j) => texts.push({ where: `question ${i + 1}, choice ${j + 1}`, text: c.text }))
  })
  const locales = [...new Set(texts.flatMap((t) => Object.keys(t.text)))].sort()
  const gaps = texts.flatMap((t) => locales.filter((l) => !(l in t.text)).map((locale) => ({ locale, where: t.where })))
  return { locales, gaps }
}

// ---------------------------------------------------------------------------
// Requests

export const SaveAnswersSchema = z.object({
  answers: z
    .array(
      z.object({
        questionId: z.string().uuid(),
        choiceId: z.string().uuid().nullable().optional(),
        ticked: z.boolean().nullable().optional(),
      }),
    )
    .max(300),
})
export type SaveAnswersInput = z.infer<typeof SaveAnswersSchema>

export const StartAttemptSchema = z.object({ locale: LangCodeSchema.optional() })

export const SignAttemptSchema = z.object({
  decision: z.enum(['pass', 'fail']),
  note: z.string().trim().max(500).optional(),
})

// An examiner records a checklist for several members at once (a group
// trained together): the same ticks for each, signed by the examiner.
export const GroupEntrySchema = z.object({
  userIds: z.array(z.string().min(1)).min(1).max(100),
  ticked: z.array(z.string().uuid()).max(300),
  decision: z.enum(['pass', 'fail']),
  // When the training took place; defaults to now.
  performedOn: DateStringSchema.optional(),
  note: z.string().trim().max(500).optional(),
})
export type GroupEntryInput = z.infer<typeof GroupEntrySchema>

export const AttemptListQuerySchema = z.object({
  from: DateStringSchema.optional(),
  to: DateStringSchema.optional(),
  userId: z.string().min(1).optional(),
  examId: z.string().uuid().optional(),
  result: z.enum(ATTEMPT_RESULTS).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
})

// ---------------------------------------------------------------------------
// Shapes the API returns

export interface ExamQuestionView {
  questionId: string
  sectionId: string | null
  kind: QuestionKind
  prompt: LocalizedText
  // Never says which is right.
  choices: { choiceId: string; label: LocalizedText }[]
}

export interface ExamSectionView {
  sectionId: string
  title: LocalizedText
}

export interface MemberExam {
  examId: string
  slug: string
  kind: ExamKind
  title: LocalizedText
  description: LocalizedText
  defaultLocale: string
  timeLimitMinutes: number | null
  resumable: boolean
  passMarkPct: number
  questionCount: number
  // Why the member cannot start now (null = they can).
  blocker: StartBlocker | null
  missingLicences: string[]
  openAttemptId: string | null
  latest: AttemptListItem | null
}

export interface AttemptListItem {
  attemptId: string
  examId: string
  examSlug: string
  examTitle: LocalizedText
  userId: string
  memberName: string
  status: AttemptStatus
  result: AttemptResult | null
  score: number | null
  maxScore: number | null
  startedAt: string
  submittedAt: string | null
  validUntil: string | null
  enteredByName: string | null
  signedByName: string | null
  signedAt: string | null
  signNote: string | null
}

export interface AttemptView {
  attempt: AttemptListItem
  exam: Pick<MemberExam, 'examId' | 'slug' | 'kind' | 'title' | 'timeLimitMinutes' | 'resumable' | 'passMarkPct'>
  locale: string
  expiresAt: string | null
  // The server's clock, so the countdown is not the phone's.
  serverNow: string
  sections: ExamSectionView[]
  questions: ExamQuestionView[]
  answers: Record<string, GivenAnswer>
  // After submission only: the key, so the member sees what they got wrong.
  correctChoices?: Record<string, string>
}

export interface ExamListResponse {
  exams: MemberExam[]
}

export interface AttemptListResponse {
  attempts: AttemptListItem[]
  total: number
}

export interface ManagedExam {
  examId: string
  slug: string
  kind: ExamKind
  title: LocalizedText
  description: LocalizedText
  defaultLocale: string
  timeLimitMinutes: number | null
  resumable: boolean
  passMarkPct: number
  allowRetake: boolean
  validMonths: number | null
  requiredLicences: string[]
  active: boolean
  publishedVersionNo: number | null
  draftVersionNo: number | null
  questionCount: number
  attemptCount: number
}

// A version's content in the import shape, with the answer key, for the
// admin's viewer and editor.
export interface ExamVersionContent {
  versionNo: number
  published: boolean
  sections: { key: string; title: LocalizedText }[]
  questions: { section?: string; text: LocalizedText; choices?: { text: LocalizedText; correct?: boolean }[] }[]
}

export interface ManagedExamDetail extends ManagedExam {
  versions: { versionNo: number; published: boolean; publishedAt: string | null }[]
  content: ExamVersionContent
  warnings: { locales: string[]; gaps: { locale: string; where: string }[] }
}
